



import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { Box, Typography } from "@mui/material";
import url from "../../url";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { MAPS_API_KEY } from "../../components/mapKey";

const LIBRARIES = ["places"]; // ✅ outside component — prevents map re-init

const getStatusIcon = (status) => {  // ✅ outside component
  const icons = {
    active: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
    inactive: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png",
    invited: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
    pending: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
    requested: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-violet.png",
  };
  return icons[status] || icons.active;
};

const AccountExecutivePins = ({
  statusFilter, dateFrom, dateTo, country, state, city, search,
  verificationStatusFilter, resetKey, selectedMapExecutiveId, onClearSelection,
}) => {
  const [executives, setExecutives] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isFullMap, setIsFullMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 30.3753, lng: 69.3451 });
  const [mapZoom, setMapZoom] = useState(5);

  const { token } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const mapRef = useRef(null); // ✅ ref to control map programmatically

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: MAPS_API_KEY,
    libraries: LIBRARIES, // ✅ stable reference
  });

  useEffect(() => {
    const fetchExecutives = async () => {
      setLoading(true);
      const statusParam = statusFilter && statusFilter !== "all" ? `&status=${statusFilter}` : "";
      const dateFromParam = dateFrom ? `&date_from=${dateFrom}` : "";
      const dateToParam = dateTo ? `&date_to=${dateTo}` : "";
      const countryParam = country ? `&country=${encodeURIComponent(country)}` : "";
      const stateParam = state ? `&province=${encodeURIComponent(state)}` : "";
      const cityParam = city ? `&city=${encodeURIComponent(city)}` : "";
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const verificationStatusParam = verificationStatusFilter && verificationStatusFilter !== "all"
        ? `&verification_status=${verificationStatusFilter}` : "";

      const apiUrl = `${url}super-admin/account-executives?sort_by=registered&sort_order=ASC&no_pagination=true`
        + statusParam + dateFromParam + dateToParam + countryParam
        + stateParam + cityParam + searchParam + verificationStatusParam;

      try {
        const response = await fetch(apiUrl, { headers: { Authorization: `Bearer ${token}` } });
        const result = await response.json();
        if (!result.error && result.data?.account_executives) {
          const data = result.data.account_executives
            .map((exec) => ({
              id: exec.id,
              name: exec.full_name,
              email: exec.email,
              status: exec.status,
              country: exec.country,
              date: exec.registered,
              lat: parseFloat(exec.latitude || exec.location_latitude),
              lng: parseFloat(exec.longitude || exec.location_longitude),
            }))
            .filter((e) => !isNaN(e.lat) && !isNaN(e.lng));
          setExecutives(data);
        } else {
          setExecutives([]);
        }
      } catch (err) {
        console.error("Error fetching AE pins:", err);
        setExecutives([]);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchExecutives();
  }, [token, statusFilter, dateFrom, dateTo, country, state, city, search, verificationStatusFilter, resetKey]);

  // ✅ Auto-center and zoom to selected pin when row is clicked from table
  useEffect(() => {
    if (selectedMapExecutiveId && executives.length > 0) {
      const selected = executives.find((e) => e.id === selectedMapExecutiveId);
      if (selected) {
        // Pan + zoom the map to the selected pin
        if (mapRef.current) {
          mapRef.current.panTo({ lat: selected.lat, lng: selected.lng });
          mapRef.current.setZoom(10); // zoom in close enough to see the pin clearly
        }
        // Also update state as fallback
        setMapCenter({ lat: selected.lat, lng: selected.lng });
        setMapZoom(10);
        // Auto-open the InfoWindow for the selected pin
        // setSelectedMarker(selected);
      }
    } else {
      // Reset to default when selection is cleared
      setMapCenter({ lat: 30.3753, lng: 69.3451 });
      setMapZoom(5);
      setSelectedMarker(null);
    }
  }, [selectedMapExecutiveId, executives]);

  const displayedExecutives = selectedMapExecutiveId
    ? executives.filter((e) => e.id === selectedMapExecutiveId)
    : executives;

  const onMapLoad = (map) => {
    mapRef.current = map; // ✅ store map instance
  };

  const renderMarkers = () => (  // ✅ function, not component — no remount issue
    <>
      {displayedExecutives.map((exec) => (
        <Marker
          key={exec.id}
          position={{ lat: exec.lat, lng: exec.lng }}
          icon={{
            url: getStatusIcon(exec.status),
            scaledSize: new window.google.maps.Size(25, 41),
            anchor: new window.google.maps.Point(12, 41),
          }}
          onClick={() => setSelectedMarker(exec)} // ✅ onClick not onMouseOver
        />
      ))}

      {selectedMarker && (
        <InfoWindow
          position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
          onCloseClick={() => setSelectedMarker(null)} // ✅ onCloseClick
        >
          <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", minWidth: "180px" }}>
            <div><b>{t("tooltip.name")}:</b> {selectedMarker.name}</div>
            <div><b>{t("tooltip.email")}:</b> {selectedMarker.email}</div>
            <div><b>{t("tooltip.status")}:</b> {selectedMarker.status}</div>
            <div><b>{t("tooltip.country")}:</b> {selectedMarker.country}</div>
          </div>
        </InfoWindow>
      )}
    </>
  );

  return (
    <Box sx={{ mt: 2 }} bgcolor={"white"}>
      <div style={{
        height: "70vh", width: "100%", marginTop: "10px",
        borderRadius: "10px", position: "relative", backgroundColor: "white",
        display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column"
      }}>

        {loading && (
          <Box sx={{
            position: "absolute", inset: 0, zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.6)",
          }}>
            <CircularProgress size={50} />
          </Box>
        )}

        {/* ✅ Normal map — unmounts when full map opens */}
        {isLoaded && !isFullMap && (
         <GoogleMap
  mapContainerStyle={{ height: "100%", width: "100%", borderRadius: "10px" }}
  center={mapCenter}
  zoom={mapZoom}
  onLoad={onMapLoad}
  options={{
    // ✅ Lock map interaction when a specific pin is selected
    gestureHandling: selectedMapExecutiveId ? "none" : "auto",
    zoomControl: !selectedMapExecutiveId,
    scrollwheel: !selectedMapExecutiveId,
    disableDoubleClickZoom: !!selectedMapExecutiveId,
    draggable: !selectedMapExecutiveId,
  }}
>
  {renderMarkers()}
</GoogleMap>
        )}

        {/* Full Map Button */}
        {!isFullMap && (
          <Box
            onClick={() => setIsFullMap(true)}
            sx={{
              position: "absolute", top: 10, right: 10, zIndex: 1000,
              backgroundColor: "#006EC2", color: "white", borderRadius: "6px",
              px: 1.5, py: 0.5, cursor: "pointer", fontSize: "14px", fontWeight: 500,
            }}
          >
            {t("fullMap")}
          </Box>
        )}

        {/* Clear Selection Button */}
        {selectedMapExecutiveId && !isFullMap && (
          <Box
            onClick={() => onClearSelection()}
            sx={{
              position: "absolute", top: 55, right: 10, zIndex: 3000,
              backgroundColor: "#fff", borderRadius: "50%", width: 36, height: 36,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
              fontSize: "18px", fontWeight: 700,
            }}
          >
            ✕
          </Box>
        )}

        {/* ✅ Full Map Modal */}
        {isFullMap && isLoaded && (
          <Box sx={{ position: "fixed", inset: 0, zIndex: 2000, backgroundColor: "#fff" }}>
            <Box
              onClick={() => setIsFullMap(false)}
              sx={{
                position: "absolute", top: 15, right: 15, zIndex: 3000,
                backgroundColor: "#fff", borderRadius: "50%", width: 36, height: 36,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
                fontSize: "18px", fontWeight: 700,
              }}
            >
              ✕
            </Box>

        <GoogleMap
  mapContainerStyle={{ height: "100%", width: "100%", borderRadius: "10px" }}
  center={mapCenter}
  zoom={mapZoom}
  onLoad={onMapLoad}
  options={{
    // ✅ Lock map interaction when a specific pin is selected
    gestureHandling: selectedMapExecutiveId ? "none" : "auto",
    zoomControl: !selectedMapExecutiveId,
    scrollwheel: !selectedMapExecutiveId,
    disableDoubleClickZoom: !!selectedMapExecutiveId,
    draggable: !selectedMapExecutiveId,
  }}
>
  {renderMarkers()}
</GoogleMap>
          </Box>
        )}

        <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
          {t("Only records with valid Latitude/Longitude are shown")}
        </Typography>
      </div>
    </Box>
  );
};

export default AccountExecutivePins;